import {useCallback, useRef, useState, useMemo} from 'react'
import PropTypes from 'prop-types'
import Drawer from '@mui/material/Drawer'
import CustomBox from '../common/CustomBox'
import {Box, CircularProgress, IconButton, Typography} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import {Root, StyledDivider, StyledSpaceBox, classes} from './FilterPanelStyle'
import {useFilters} from '../../store/hooks/filters'
import {useSelector} from 'react-redux'
import {useFilterManagement} from '../../store/hooks/useFilterManagement'
import {useFilterBusinessLogic} from '../../store/hooks/useFilterBusinessLogic'
import {useUseCases} from '../../store/hooks/useCase'
import {
  handleUseCaseOnFilterRemove,
  updateUseCaseOnClearFilters
} from '../../AdminPanel/ProjectDetails/Helper/UseCaseHelper'

import buttonTitles from '../../appConfig/buttonTitles.config.json'
import FilterCategoryTabs from './FilterCategoryTabs'
import FilterCategorySection from './FilterCategorySection'
import FilterTabPanel from './FilterTabPanel'
import {selectFilterConfig, selectIsConfigLoading} from '../../store/slices/configSlice'
import {
  TabsContainer,
  TabPanelContainer,
  FilterContainer,
  FilterItemContainer,
  EmptyState
} from './FilterTabsAndLayoutStyles'
import FilterControl, {OutreachFiltersGroup} from '../common/FilterControl'
import {getFilterCustomName, getCategoryCustomName} from '../../utils/filterManagementHelpers'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import {selectCustomizations} from '../../store/slices/filterManagementSlice'

import GlobalSearch from './GlobalSearch'
import SavedFiltersPopup from './SaveFilters/SavedFiltersPopup'
import SaveFiltersBar from './SaveFilters/SaveFiltersBar'

const FilterPanelComponent = ({classesName, handleFilterPanelClick}) => {
  const {filtersOpen, setFiltersOpen} = useFilters()
  const {useCases, setUseCases, setIsSnackBarOpen, setSnackbarMessage} = useUseCases()
  const {
    totalAddedFiltersCount,
    categoryFilterCounts,
    removeFilter,
    setFilterValue,
    clearAllFilters,
    getFilterValue,
    isFilterAdded,
    isFilterApplied,
    addedFilters,
    clearAllPendingFilterValues
  } = useFilterManagement()
  const {isFilterDisabled, getDisabledReason} = useFilterBusinessLogic()
  const filterConfig = useSelector(selectFilterConfig)
  const configLoading = useSelector(selectIsConfigLoading)
  const customizations = useSelector(selectCustomizations)

  const [currentTab, setCurrentTab] = useState(0)

  const closeIconRef = useRef(null)
  const tabsContainerRef = useRef(null)

  const closeIconLeftClick = useCallback(
    (event) => {
      if (event.key === 'Tab' && event.shiftKey) {
        handleFilterPanelClick(event, 'shiftKey')
      }
    },
    [handleFilterPanelClick]
  )

  const enrichedConfig = useMemo(() => {
    if (!filterConfig?.data) return filterConfig

    return {
      ...filterConfig,
      data: filterConfig.data.map((category) => ({
        ...category,
        customName: getCategoryCustomName(category.id, customizations),
        filters: category.filters.map((filter) => ({
          ...filter,
          customName: getFilterCustomName(filter.key, customizations)
        }))
      }))
    }
  }, [filterConfig, customizations])

  const categories = useMemo(() => {
    return enrichedConfig?.data || []
  }, [enrichedConfig])

  const handleTabChange = (newTab) => {
    setCurrentTab(newTab)
  }

  const handleClearAllFilters = () => {
    const clearedFilterKeys = [...addedFilters]

    if (clearedFilterKeys.length > 0) {
      const {updatedUseCases} = updateUseCaseOnClearFilters(useCases, clearedFilterKeys)
      setUseCases(updatedUseCases)
    }

    clearAllPendingFilterValues()
    clearAllFilters()
  }

  const renderFilter = (filter, categoryId = null) => {
    if (!isFilterAdded(filter.key)) {
      return null
    }

    const filterValue = getFilterValue(filter.key)
    const hasValue = isFilterApplied(filter.key)
    const filterDisabled = isFilterDisabled(filter.key)
    const disabledReason = getDisabledReason(filter.key)

    const handleFilterChange = (newValue) => {
      setFilterValue(filter.key, newValue)
    }

    const handleFilterRemove = () => {
      handleUseCaseOnFilterRemove(filter.key, useCases, setUseCases, setIsSnackBarOpen, setSnackbarMessage)
      removeFilter(filter.key)
    }

    return (
      <FilterItemContainer key={filter.key}>
        <FilterControl
          filter={{
            ...filter,
            value: filterValue,
            isActive: hasValue,
            disabled: filterDisabled,
            disabledReason,
            categoryId
          }}
          onChange={handleFilterChange}
          onRemove={handleFilterRemove}
        />
      </FilterItemContainer>
    )
  }

  const renderCategoryFilters = (category) => {
    const addedCategoryFilters = category.filters.filter((filter) => isFilterAdded(filter.key))

    if (addedCategoryFilters.length === 0) {
      return null
    }

    const isOutreachCategory = category.isMutuallyExclusive && addedCategoryFilters.some((f) => f.type === 'outreach')

    if (isOutreachCategory) {
      const outreachFilters = addedCategoryFilters.filter((f) => f.type === 'outreach')
      const outreachValues = {}
      for (const f of outreachFilters) {
        outreachValues[f.key] = getFilterValue(f.key)
      }

      const handleOutreachChange = (filterKey, newValue) => {
        setFilterValue(filterKey, newValue)
      }

      const handleOutreachRemove = (filterKey) => {
        handleUseCaseOnFilterRemove(filterKey, useCases, setUseCases, setIsSnackBarOpen, setSnackbarMessage)
        removeFilter(filterKey)
      }

      const nonOutreachFilters = addedCategoryFilters.filter((f) => f.type !== 'outreach')

      return (
        <Box>
          <FilterContainer>
            {nonOutreachFilters.map((filter) => renderFilter(filter, category.id))}
            {outreachFilters.length > 0 && (
              <FilterItemContainer>
                <OutreachFiltersGroup
                  filters={outreachFilters}
                  values={outreachValues}
                  onFilterChange={handleOutreachChange}
                  onFilterRemove={handleOutreachRemove}
                />
              </FilterItemContainer>
            )}
          </FilterContainer>
        </Box>
      )
    }

    return (
      <Box>
        <FilterContainer>{addedCategoryFilters.map((filter) => renderFilter(filter, category.id))}</FilterContainer>
      </Box>
    )
  }

  return (
    <Root>
      <Drawer
        className={`${classes.drawer} ${classesName}`}
        variant='persistent'
        anchor='left'
        open={filtersOpen}
        classes={{
          paper: classes.drawerPaper
        }}
      >
        <CustomBox tabIndex={0} display='flex' flexDirection='column' height='100%' aria-label='Filters'>
          {/* Fixed Header Section */}
          <Box>
            <div className={classes.mainFiltersContainer}>
              <StyledDivider />
              <CustomBox display='flex' alignItems='center' justifyContent='space-between' width='100%'>
                <Box display='flex' alignItems='center' gap={1}>
                  <FilterAltIcon sx={{fontSize: (theme) => theme.typography.h5.fontSize, color: 'text.secondary'}} />
                  <Typography className={classes.filterTitle}>FILTERS</Typography>
                </Box>
                <Box>
                  <IconButton
                    ref={closeIconRef}
                    test-id='close-filter-panel'
                    id='close-filters'
                    aria-label='close-filters'
                    className={classes.closeButton}
                    onClick={() => setFiltersOpen(!filtersOpen)}
                    onKeyDown={closeIconLeftClick}
                    size='large'
                    title={buttonTitles.filterpanelClose}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              </CustomBox>
            </div>
            <Box display='flex' alignItems='center' pr={1}>
              <Box flex={1}>
                <GlobalSearch />
              </Box>
              <SavedFiltersPopup />
            </Box>

            {configLoading && (
              <Box display='flex' justifyContent='center' alignItems='center' p={4}>
                <CircularProgress />
              </Box>
            )}

            {!configLoading && enrichedConfig?.data && (
              <TabsContainer ref={tabsContainerRef}>
                <FilterCategoryTabs
                  value={currentTab}
                  onChange={handleTabChange}
                  categories={enrichedConfig.data}
                  categoryFilterCounts={categoryFilterCounts}
                />
              </TabsContainer>
            )}

            {!configLoading && !enrichedConfig?.data && (
              <EmptyState>
                <p>Error loading filter configuration</p>
              </EmptyState>
            )}
          </Box>

          {/* Scrollable Content Section */}
          {!configLoading && enrichedConfig?.data && (
            <TabPanelContainer>
              <FilterTabPanel value={currentTab} index={0} categoryId='all'>
                {categories.map((category) => (
                  <FilterCategorySection
                    key={category.id}
                    category={category}
                    selectedCount={categoryFilterCounts[category.id] || 0}
                    tabsContainerRef={tabsContainerRef}
                  >
                    {renderCategoryFilters(category)}
                  </FilterCategorySection>
                ))}
              </FilterTabPanel>

              {enrichedConfig.data.map((category, index) => {
                const tabIndex = index + 1

                return (
                  <FilterTabPanel key={category.id} value={currentTab} index={tabIndex} categoryId={category.id}>
                    <FilterCategorySection
                      category={category}
                      selectedCount={categoryFilterCounts[category.id] || 0}
                      tabsContainerRef={tabsContainerRef}
                    >
                      {renderCategoryFilters(category)}
                    </FilterCategorySection>
                    <StyledSpaceBox />
                  </FilterTabPanel>
                )
              })}
            </TabPanelContainer>
          )}

          {/* Fixed Footer */}
          {totalAddedFiltersCount > 0 && <SaveFiltersBar clearAllFilters={handleClearAllFilters} />}
        </CustomBox>
      </Drawer>
    </Root>
  )
}

FilterPanelComponent.propTypes = {
  classesName: PropTypes.string.isRequired,
  handleFilterPanelClick: PropTypes.func.isRequired
}

export default FilterPanelComponent

/**
 * FilterPanelComponent
 *
 * A React component that provides a clean, unified filter management interface.
 * Uses the new filterManagement slice to handle all filter selections across categories.
 *
 * @component
 *
 * @param {Object} props - The component props.
 * @param {string} props.classesName - Additional class names for styling the component.
 * @param {Function} props.handleFilterPanelClick - Callback function to handle filter panel click events.
 *
 * Features:
 * - Unified filter state management across all categories
 * - Search functionality to find filters quickly
 * - Tabbed interface for organizing filters by category
 * - Support for multiple filter types (range, checkbox, toggle)
 * - Business logic for disabling filters based on state selection
 * - Active filter count display
 * - Clean, maintainable code structure
 *
 * @returns {JSX.Element} The rendered FilterPanelComponent.
 */
